<template>
  <div v-if="this.$vuetify.breakpoint.smAndUp">
    <div v-if="showAppLoading" class="fullloadingscreen">
      <AppLoadingScreen />
    </div>
    <router-view v-if="showAppShell"></router-view>
    <div
      v-if="loginVisible"
      ref="loginLayer"
      class="login-layer"
      :class="{
        'login-layer--front': loginCovering,
        'login-layer--leave': loginLeaving,
      }"
      data-testid="login-layer"
    >
      <Login />
    </div>
  </div>
  <div v-else>
    <div class="smallsize" style="height:100vh">
      <img src="/img/consark/ConsarcLight.svg" class="logo-image" alt="Consarc.ai" width="220" height="33" style="width:220px;max-width:220px;height:auto;"/>
      <span class="pt-4">This application doesn't work on mobile screen size.<br/>Please use a tablet or laptop to proceed</span>
      <a href="consark.ai" class="link">Goto Website</a>
    </div>
  </div>
</template>

<script>
import Login from './views/Auth/Login.vue';
import AppLoadingScreen from './views/common/AppLoadingScreen.vue';
import constants from './plugins/constants';

const LOGIN_SLIDE_MS = 600;
const LOADING_SCREEN_HOLD_MS = 2500;

export default {
  components: { Login, AppLoadingScreen },
  name: 'App',

  data: () => ({
    pass: false,
    holdLoader: LOADING_SCREEN_HOLD_MS > 0,
    loginVisible: true,
    loginCovering: false,
    loginLeaving: false,
    loginExiting: false,
  }),
  computed: {
    loading() {
      return this.$store.state.loading;
    },
    showAppLoading() {
      return this.loading > 0 || this.holdLoader || this.loginExiting;
    },
    showAppShell() {
      return (this.pass || this.$store.state.login) && !this.loginVisible;
    },
  },
  created: function(){
    this.reloadData();
    this.syncLoginLayerFromAuth();
  },
  mounted() {
    document.body.classList.add('app-mounted');
    const splash = document.querySelector('.fullloadingscreen1');
    if (splash && splash.parentNode) {
      splash.parentNode.removeChild(splash);
    }
    if (LOADING_SCREEN_HOLD_MS > 0) {
      this._holdLoaderTimer = window.setTimeout(() => {
        this.holdLoader = false;
        this._holdLoaderTimer = null;
      }, LOADING_SCREEN_HOLD_MS);
    }
  },
  beforeDestroy() {
    if (this._holdLoaderTimer) {
      window.clearTimeout(this._holdLoaderTimer);
      this._holdLoaderTimer = null;
    }
    this.clearLoginSlideTimer();
    this.clearLoginSlideLock();
  },
  methods: {
    prefersReducedMotion() {
      return Boolean(
        typeof window !== 'undefined' &&
        window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      );
    },
    canSlideLoginAway() {
      return this.loginVisible && !this.pass && this.loading < 1;
    },
    syncLoginLayerFromAuth() {
      if (this.pass || this.$store.state.login) {
        this.hideLoginLayer();
        return;
      }
      this.resetLoginLayer();
    },
    startLoginSlide() {
      if (this.loginLeaving || !this.loginVisible) return;
      this.clearLoginSlideTimer();
      this.loginExiting = true;
      this.loginCovering = true;
      document.documentElement.classList.add('login-slide-lock');
      document.body.classList.add('login-slide-lock');
      this.$nextTick(() => {
        const layer = this.$refs.loginLayer;
        if (layer) {
          void layer.offsetWidth;
        }
        if (this.prefersReducedMotion()) {
          this.hideLoginLayer();
          return;
        }
        window.requestAnimationFrame(() => {
          if (!this.loginVisible || !this.loginCovering) return;
          this.loginLeaving = true;
          this._loginSlideTimer = window.setTimeout(() => {
            this._loginSlideTimer = null;
            this.hideLoginLayer();
          }, LOGIN_SLIDE_MS);
        });
      });
    },
    hideLoginLayer() {
      this.clearLoginSlideTimer();
      this.loginVisible = false;
      this.loginLeaving = false;
      this.loginCovering = false;
      this.$nextTick(() => {
        this.loginExiting = false;
        this.clearLoginSlideLock();
      });
    },
    resetLoginLayer() {
      this.clearLoginSlideTimer();
      this.loginVisible = true;
      this.loginLeaving = false;
      this.loginCovering = false;
      this.loginExiting = false;
      this.clearLoginSlideLock();
    },
    clearLoginSlideTimer() {
      if (this._loginSlideTimer) {
        window.clearTimeout(this._loginSlideTimer);
        this._loginSlideTimer = null;
      }
    },
    clearLoginSlideLock() {
      document.documentElement.classList.remove('login-slide-lock');
      document.body.classList.remove('login-slide-lock');
    },
    dismissBootLoading() {
      if (this.$store.state.loading > 0) {
        this.$store.commit('removeLoading');
      }
    },
    onAuthStateForLoginLayer() {
      if (this.pass) {
        this.hideLoginLayer();
        return;
      }
      if (this.$store.state.login) {
        if (this.canSlideLoginAway()) {
          this.startLoginSlide();
          return;
        }
        this.hideLoginLayer();
        return;
      }
      this.resetLoginLayer();
    },
    reloadData: function(){
      let oldpass = this.pass;
      const pathname = (window.location.pathname || '').replace(/\/+$/, '') || '/';
      const exactPublicPaths = [
        '/setpassword',
        '/setnewpassword',
        '/forgotpassword',
        '/selfservice',
        constants.SSO.CALLBACK_PATH,
        constants.SSO.LOGOUT_COMPLETE_PATH,
      ].map((p) => (p || '').replace(/\/+$/, '') || '/');
      if (pathname === '/pbc/requestee' || pathname.startsWith('/pbc/requestee/')) oldpass = true;
      else if (exactPublicPaths.indexOf(pathname) > -1) oldpass = true;
      else oldpass = false;
      if(oldpass !== this.pass) {
        this.pass = oldpass;
        if(this.pass){
          this.$store.commit("changeNoAuth", true);
          this.$store.commit("changeLogin", true);
          this.dismissBootLoading();
        }else{
          this.$store.commit("changeNoAuth", false);
          this.$store.commit("changeLogin", false);
        }
      } else if (this.pass) {
        this.dismissBootLoading();
      }
    },
  },
  watch: {
    $route() {
      this.reloadData();
    },
    pass() {
      this.onAuthStateForLoginLayer();
    },
    '$store.state.login'() {
      this.onAuthStateForLoginLayer();
    },
  }
};
</script>
